import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { LEGAL_ENTITY, getSubProcessors } from '../config/legalEntity';

const PrivacyPolicy = ({ source }) => {
  const navigate = useNavigate();

  const hasSourceCompliance = source && (source.registered_company_name || source.fca_number);
  const dpEmail = source?.data_protection_email || LEGAL_ENTITY.privacyEmail;
  const linkColor = source?.primary_color || '#3B1578';

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="privacy-policy-page">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6 text-slate-600 hover:text-slate-900"
        data-testid="privacy-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 space-y-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500">Last updated: 24 May 2026</p>
        <p className="text-sm text-slate-500">This service is intended for UK residents aged 18 or over.</p>

        {/* Section 1 — Platform & Data Controller */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">1. Who We Are</h2>
          <p className="text-slate-600 leading-relaxed">
            This vehicle finance application form is powered by <strong>ApplyDrive.uk</strong>, a technology platform that facilitates the submission of vehicle finance applications on behalf of authorised finance brokers and dealers.
          </p>
          <p className="text-slate-600 leading-relaxed">
            ApplyDrive.uk acts as a <strong>data processor</strong> — we provide the technology used to collect, validate, and transmit your application data. We do not make lending decisions, provide financial advice, or act as a credit broker.
          </p>
          {hasSourceCompliance && (
            <p className="text-slate-600 leading-relaxed">
              The <strong>data controller</strong> for your application is <strong>{source.registered_company_name || source.company_name || source.name}</strong>, the finance broker or dealer whose branded portal you are using. Their details are provided in Section 2 below.
            </p>
          )}
        </section>

        {/* Section 2 — Data Controller Details */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">2. Data Controller</h2>
          {hasSourceCompliance ? (
            <>
              <p className="text-slate-600 leading-relaxed">
                The data controller responsible for your personal data is:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 text-slate-600 space-y-1">
                <p><strong>{source.registered_company_name || source.company_name || source.name}</strong></p>
                {source.company_registration_number && <p>Company Registration: {source.company_registration_number}</p>}
                {source.registered_address && <p>Registered Office: {source.registered_address}</p>}
                {source.fca_number && <p>FCA Registration: {source.fca_number}{source.fca_status ? ` (${source.fca_status})` : ''}</p>}
                {source.ico_registration && <p>ICO Registration: {source.ico_registration}</p>}
                {source.data_protection_email && (
                  <p>Data Protection: <a href={`mailto:${source.data_protection_email}`} style={{ color: linkColor }} className="hover:underline">{source.data_protection_email}</a></p>
                )}
                {source.website_url && (
                  <p>Website: <a href={source.website_url} style={{ color: linkColor }} className="hover:underline" target="_blank" rel="noopener noreferrer">{source.website_url}</a></p>
                )}
              </div>
            </>
          ) : (
            <div className="bg-slate-50 rounded-lg p-4 text-slate-600 space-y-1">
              <p><strong>ApplyDrive.uk</strong></p>
              <p>Data Protection: <a href="mailto:privacy@applydrive.uk" style={{ color: linkColor }} className="hover:underline">privacy@applydrive.uk</a></p>
            </div>
          )}
        </section>

        {/* Section 3 — Platform Provider */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">3. Platform Provider</h2>
          <p className="text-slate-600 leading-relaxed">
            This application form is operated by <strong>{LEGAL_ENTITY.tradingName}</strong>, a trading name of <strong>{LEGAL_ENTITY.companyName}</strong>. {LEGAL_ENTITY.companyName} acts as a data processor on behalf of the data controller identified above.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-slate-600 space-y-1">
            <p><strong>{LEGAL_ENTITY.companyName}</strong> (trading as {LEGAL_ENTITY.tradingName})</p>
            <p>Registered in {LEGAL_ENTITY.jurisdiction}, Company Number: <a href={LEGAL_ENTITY.companiesHouseUrl} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.companyNumber}</a></p>
            <p>Registered Office: {LEGAL_ENTITY.registeredOffice}</p>
            <p>ICO Registration: <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.icoNumber}</a></p>
            <p>Role: Data Processor (Technology Platform)</p>
            <p>Contact: <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.privacyEmail}</a></p>
          </div>
        </section>

        {/* Section 4 — Ephemeral Processing */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">4. How This Application Processes Your Data</h2>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-slate-700 leading-relaxed font-medium">
              This application uses ephemeral processing. Your personal data is never stored on our servers or in any database. It exists only during your active browser session and is transmitted directly to your finance broker via our secure finance proposal system when you submit your application.
            </p>
          </div>
          <p className="text-slate-600 leading-relaxed">This means:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li>Your personal information is held <strong>only in your browser</strong> while you complete the form</li>
            <li>If you close the browser or navigate away before submitting, your data is permanently lost</li>
            <li>We do not write your personal data to any database, log file, or persistent storage</li>
            <li>We retain only anonymous, non-identifiable usage statistics (e.g., number of applications submitted)</li>
          </ul>
        </section>

        {/* Section 5 — Data Collected */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">5. What Data We Collect</h2>
          <p className="text-slate-600 leading-relaxed">When you use this application form, we collect the following information during your session:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li><strong>Personal details:</strong> Name, date of birth, nationality, marital status, number of dependants</li>
            <li><strong>Contact information:</strong> Mobile number, email address</li>
            <li><strong>Address history:</strong> Current and previous addresses (minimum 3 years), including residential status</li>
            <li><strong>Employment details:</strong> Employer name, address, job title, income, employment history</li>
            <li><strong>Financial information:</strong> Bank sort code and account number</li>
            <li><strong>Vehicle details:</strong> Registration, mileage, type, cash price</li>
            <li><strong>Finance details:</strong> Deposit, part exchange, settlement figure, finance type (HP/PCP), term</li>
            <li><strong>Business details:</strong> (For business applications) Company name, registration number, VAT number, date of incorporation</li>
            <li><strong>Identification:</strong> Driving licence type, passport availability</li>
            <li><strong>Uploaded documents:</strong> If using the staff upload feature, documents provided for OCR extraction are processed in real-time and are not stored</li>
          </ul>
          <p className="text-slate-600 leading-relaxed pt-2 text-sm">
            We do not knowingly collect special category personal data (such as information about health, race, religion, political opinions, or criminal records) through this platform. Please do not submit such information through the form.
          </p>
        </section>

        {/* Section 6 — How We Use Data */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">6. How We Use Your Data</h2>
          <p className="text-slate-600 leading-relaxed">Your personal data is processed for the following purposes:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li><strong>Finance application submission:</strong> To transmit your application to your finance broker via our secure finance proposal system for processing by their lending partners</li>
            <li><strong>OCR text extraction:</strong> (Staff upload only) To automatically populate form fields from uploaded documents using AI processing. Documents are processed in real-time and are not stored</li>
            <li><strong>Address verification:</strong> To validate and look up UK postcodes. Only the postcode is sent — no personal data is shared</li>
            <li><strong>Vehicle verification:</strong> To look up vehicle details via the DVLA. Only the registration number is sent</li>
          </ul>
          <p className="text-slate-600 leading-relaxed pt-2 text-sm">
            <strong>Automated decision-making:</strong> ApplyDrive itself does not make lending decisions, perform credit scoring, or carry out automated profiling. Once your application is submitted, your finance broker and their lending partners may use automated systems (including automated credit scoring and fraud checks) to assess your application — see their privacy notices for details. You have the right to request a manual review of any decision made solely by automated means.
          </p>
          <p className="text-slate-600 leading-relaxed pt-2 text-sm">
            <strong>Marketing:</strong> ApplyDrive does not use your personal data for marketing purposes. We do not send marketing emails, SMS, or post, and we do not share your data with marketing agencies. Any marketing communications you receive in connection with your application come directly from your finance broker or chosen lender, governed by their own privacy and marketing notices.
          </p>
        </section>

        {/* Section 7 — Legal Basis */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">7. Legal Basis for Processing</h2>
          <p className="text-slate-600 leading-relaxed">Your data is processed based on:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li><strong>Contractual necessity (Article 6(1)(b) UK GDPR):</strong> Processing is necessary to fulfil your vehicle finance application request.</li>
            <li><strong>Consent (Article 6(1)(a) UK GDPR):</strong> By submitting your application, you consent to your data being shared with your finance broker and their panel of lending partners.</li>
            <li><strong>Legal obligation (Article 6(1)(c) UK GDPR):</strong> Compliance with FCA regulations and anti-money laundering requirements.</li>
            <li><strong>Legitimate interest (Article 6(1)(f) UK GDPR):</strong> To improve our services and prevent fraud.</li>
          </ul>
        </section>

        {/* Section 8 — Data Sharing & Sub-processors */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">8. Who We Share Your Data With</h2>
          <p className="text-slate-600 leading-relaxed">When you submit your application, your data is shared with:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li><strong>Your finance broker ({source?.name || 'the data controller'}):</strong> Your complete application is submitted via our secure finance proposal system to their panel of lending partners</li>
            <li><strong>Lending partners:</strong> Finance companies on your broker's panel will receive your application and may assess your eligibility for vehicle finance</li>
            <li><strong>Credit Reference Agencies:</strong> Lending partners may share your information with Credit Reference Agencies (see Section 9)</li>
            <li><strong>Fraud Prevention Agencies:</strong> Lending partners may also share your information with Fraud Prevention Agencies (e.g. CIFAS, SIRA, National Hunter) to verify your identity and prevent financial crime. These agencies may retain your records for up to 6 years</li>
          </ul>

          <p className="text-slate-600 leading-relaxed pt-2">
            <strong>Joint Applicants and Guarantors</strong> — If you submit personal information about another person as part of your application (for example, a joint applicant, a director acting as guarantor, or any other third party), you confirm that you have their explicit consent to share their data with us, your finance broker, and their lending partners. You are responsible for making them aware of how their data will be used as described in this Privacy Policy.
          </p>

          <p className="text-slate-600 leading-relaxed pt-2">
            <strong>Sub-processors</strong> — In line with UK GDPR Article 28, we maintain a transparent list of all third-party service providers that may process your data on our behalf to operate the platform:
          </p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Sub-processor</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Role</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-700">Data Residency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {getSubProcessors(source).map((sp) => (
                  <tr key={sp.name}>
                    <td className="px-3 py-2 text-slate-700 font-medium align-top">{sp.name}</td>
                    <td className="px-3 py-2 text-slate-600 align-top">{sp.role}</td>
                    <td className="px-3 py-2 text-slate-600 align-top">{sp.dataResidency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Each sub-processor is bound by appropriate data protection agreements (Data Processing Addendums) and is only sent the minimum data required to perform its function. We do not sell your personal data. We do not share your data for marketing purposes without your express written consent.
          </p>
        </section>

        {/* Section 9 — CRAs */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">9. Credit Reference Agencies</h2>
          <p className="text-slate-600 leading-relaxed">
            Once your application is submitted and passed to lending partners, those lenders may supply your personal information to Credit Reference Agencies (CRAs). CRAs will provide information about you, such as your financial history, to assess creditworthiness and product suitability, check your identity, manage your account, trace and recover debts, and prevent criminal activity.
          </p>
          <p className="text-slate-600 leading-relaxed">
            The identities of the CRAs and the ways in which they use and share personal information are explained in more detail at:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li>TransUnion: <a href="https://www.transunion.co.uk/crain" style={{ color: linkColor }} className="hover:underline" target="_blank" rel="noopener noreferrer">www.transunion.co.uk/crain</a></li>
            <li>Equifax: <a href="https://www.equifax.co.uk/crain" style={{ color: linkColor }} className="hover:underline" target="_blank" rel="noopener noreferrer">www.equifax.co.uk/crain</a></li>
            <li>Experian: <a href="https://www.experian.co.uk/crain" style={{ color: linkColor }} className="hover:underline" target="_blank" rel="noopener noreferrer">www.experian.co.uk/crain</a></li>
          </ul>
        </section>

        {/* Section 10 — Data Retention */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">10. Data Retention</h2>
          <p className="text-slate-600 leading-relaxed">
            <strong>On this platform:</strong> We do not retain any personal data. All processing is ephemeral — your information exists only during your browser session and is not stored on our servers.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <strong>After submission:</strong> Once submitted, your data is held by your finance broker and their lending partners in accordance with their own data retention policies. Lending partners and CRAs typically retain records for up to <strong>6 years</strong> from the date of your last interaction, in line with regulatory requirements.
          </p>
          <p className="text-slate-600 leading-relaxed">
            To request deletion of data held by your broker or lending partner, contact them directly (see Section 12).
          </p>
        </section>

        {/* Section 11 — Consent */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">11. Consent and Withdrawal</h2>
          <p className="text-slate-600 leading-relaxed">
            By submitting your application through this form, you consent to your personal data being transmitted to your finance broker and their lending partners for the purpose of arranging vehicle finance.
          </p>
          <p className="text-slate-600 leading-relaxed">
            You may withdraw your consent at any time by contacting <a href={`mailto:${dpEmail}`} style={{ color: linkColor }} className="hover:underline">{dpEmail}</a>.
          </p>
          <p className="text-slate-600 leading-relaxed font-medium">
            Please note: If your details have already been submitted or passed on to a lender or Credit Reference Agency, you will need to contact them directly to exercise your rights regarding that data, as we do not retain a copy.
          </p>
        </section>

        {/* Section 12 — Your Rights */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">12. Your Rights (UK GDPR)</h2>
          <p className="text-slate-600 leading-relaxed">You have the following rights regarding your personal data:</p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            <li><strong>Right of Access:</strong> Request a copy of all personal data held about you</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate personal data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal data (the Right to be Forgotten)</li>
            <li><strong>Right to Restriction:</strong> Request that we limit how we use your data</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a portable, machine-readable format</li>
            <li><strong>Right to Object:</strong> Object to processing of your data for marketing or other purposes</li>
          </ul>
          <p className="text-slate-600 leading-relaxed">
            As we use ephemeral processing and do not store your personal data, most rights requests regarding data held by us will confirm that no data is retained. For data submitted to lending partners, please contact:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
            {hasSourceCompliance && (
              <li><strong>{source.name}:</strong> <a href={`mailto:${source.data_protection_email || dpEmail}`} style={{ color: linkColor }} className="hover:underline">{source.data_protection_email || dpEmail}</a></li>
            )}
            <li><strong>{LEGAL_ENTITY.companyName} (Platform):</strong> <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.privacyEmail}</a></li>
          </ul>
        </section>

        {/* Section 13 — Data Security */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">13. Vulnerable Customers</h2>
          <p className="text-slate-600 leading-relaxed">
            As a technology platform, ApplyDrive does not make lending or suitability decisions and does not deal with customers directly during the application process. Identification and support of vulnerable customers, in line with FCA guidance (FG21/1) and the Consumer Duty, is the responsibility of your regulated finance broker and chosen lender. If you require accessibility assistance to complete an application, please contact your broker, who can complete the form on your behalf.
          </p>
        </section>

        {/* Section 14 — Data Security */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">14. Data Security</h2>
          <p className="text-slate-600 leading-relaxed">
            We take all reasonable technical and organisational precautions to protect your personal information. This includes encrypted connections (HTTPS/TLS) for all data transmission, secure UK-hosted server infrastructure, and restricted access to any operational data. Your personal data is transmitted via encrypted channels and is never written to our servers.
          </p>
        </section>

        {/* Section 15 — Cookies */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">15. Cookies</h2>
          <p className="text-slate-600 leading-relaxed">
            This application uses only essential cookies required for the form to function correctly. We do not use tracking cookies, analytics cookies, or advertising cookies. No cookies on this application collect or store personal data.
          </p>
        </section>

        {/* Section 16 — Data Transfers */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">16. International Data Transfers</h2>
          <p className="text-slate-600 leading-relaxed">
            This application is hosted on UK-based servers. Your personal data is processed and transmitted within the United Kingdom. We do not transfer your personal data outside of the UK except where necessary for OCR document processing, in which case appropriate safeguards are in place in accordance with UK GDPR requirements.
          </p>
        </section>

        {/* Section 17 — Complaints */}
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-slate-800">17. Complaints</h2>
          <p className="text-slate-600 leading-relaxed">
            If you are dissatisfied with any aspect of our handling of your personal data, please first contact us at <a href={`mailto:${LEGAL_ENTITY.privacyEmail}`} style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.privacyEmail}</a>. We will respond within 30 days.
          </p>
          <p className="text-slate-600 leading-relaxed">
            If you remain dissatisfied, you have the right to lodge a complaint with the Information Commissioner's Office (ICO). {LEGAL_ENTITY.companyName} is registered with the ICO under registration number{' '}
            <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.icoNumber}</a>.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-1">
            <p><strong>Information Commissioner's Office (ICO)</strong></p>
            <p>Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF</p>
            <p>Phone: 0303 123 1113</p>
            <p>Website: <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">ico.org.uk/make-a-complaint</a></p>
          </div>
        </section>

        {/* Regulatory Information — Source specific */}
        {hasSourceCompliance && (
          <section className="space-y-3 border-t border-slate-200 pt-6">
            <h2 className="text-xl font-semibold text-slate-800">Regulatory Information</h2>
            {source.fca_number && (
              <p className="text-slate-600 leading-relaxed text-sm">
                {source.registered_company_name || source.name}{source.fca_status ? ` is ${source.fca_status}` : ''} (FCA registration number {source.fca_number}). Permitted activities include acting as a credit broker, not a lender.
              </p>
            )}
            <p className="text-slate-600 leading-relaxed text-sm">
              We can introduce you to {source.name}. We do not charge a fee for our Consumer Credit services. We do not act as a financial adviser or fiduciary. We act in our own interest and will typically receive commission from lenders based on either a fixed fee or a fixed percentage of the amount you borrow. Any and all commission amounts will be fully disclosed to you as part of your application journey.
            </p>
            <p className="text-slate-600 leading-relaxed text-sm">
              All finance applications are subject to status, terms and conditions apply, UK residents only, 18s or over. Guarantees may be required.
            </p>
          </section>
        )}

        {/* Platform footer */}
        <div className="border-t border-slate-200 pt-4 text-center space-y-2">
          <p className="text-xs text-slate-500">
            {LEGAL_ENTITY.tradingName} is a trading name of <strong>{LEGAL_ENTITY.companyName}</strong>, a company registered in {LEGAL_ENTITY.jurisdiction} (Company No. <a href={LEGAL_ENTITY.companiesHouseUrl} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.companyNumber}</a>). Registered office: {LEGAL_ENTITY.registeredOffice}. ICO Registration: <a href={LEGAL_ENTITY.icoRegisterUrl} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }} className="hover:underline">{LEGAL_ENTITY.icoNumber}</a>.
          </p>
          <p className="text-xs text-slate-400">Powered by ApplyDrive.uk — Vehicle finance applications, simplified.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
